import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { forkJoin } from 'rxjs';
import { filter, finalize } from 'rxjs/operators';
import { CurrentContextService } from '../../../core/context/current-context.service';
import {
  LiveUpdateEventTypes,
  growthSearchOpsTopic,
  liveStatusTranslationKey
} from '../../../core/live-updates/live-update.models';
import { LiveUpdateService } from '../../../core/live-updates/live-update.service';
import { LocalizationService } from '../../../core/localization/localization.service';
import { AinFormFieldComponent } from '../../../shared/design-system/form-field.component';
import { InfoHintComponent } from '../../../shared/design-system/info-hint.component';
import { JalaliDateInputComponent } from '../../../shared/design-system/jalali-date-input.component';
import { AinPageComponent } from '../../../shared/design-system/page.component';
import { AinStateComponent } from '../../../shared/design-system/state.component';
import { StructuredJsonDisplayComponent } from '../../../shared/design-system/structured-json-display.component';
import { StructuredJsonFormComponent } from '../../../shared/design-system/structured-json-form.component';
import {
  SEARCH_OPS_ALGO_ENGINES,
  SEARCH_OPS_SCORECARD_SOURCES,
  SearchAlgoLogEntryDto,
  SearchAnomalyDto,
  SearchPropertyListItemDto,
  SearchScorecardBoardDto,
  searchOpsAlgoEffectFields,
  searchOpsAnomalyHypothesisFields,
  searchOpsScorecardMetricFields
} from './growth-search-ops.models';
import { GrowthSearchOpsService } from './growth-search-ops.service';

@Component({
  standalone: true,
  selector: 'ain-growth-search-ops-scorecard',
  imports: [
    AinPageComponent,
    AinStateComponent,
    InfoHintComponent,
    AinFormFieldComponent,
    FormsModule,
    ButtonModule,
    InputTextModule,
    SelectModule,
    TableModule,
    RouterLink,
    JalaliDateInputComponent,
    StructuredJsonFormComponent,
    StructuredJsonDisplayComponent
  ],
  template: `
    <ain-page
      [eyebrow]="t('growth.eyebrow')"
      [title]="t('growth.searchops.scorecard.title')"
      [description]="t('growth.searchops.scorecard.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'growth.searchops.scorecard.help.title'"
        [descriptionKey]="'growth.searchops.scorecard.help.description'"
        [bulletKeys]="[
          'growth.searchops.scorecard.help.bullets.metrics',
          'growth.searchops.scorecard.help.bullets.compare',
          'growth.searchops.scorecard.help.bullets.anomaly'
        ]" />

      <div class="toolbar">
        <button pButton type="button" severity="secondary" [label]="t('common.refresh')" (click)="load(true)"></button>
        <a pButton severity="secondary" [routerLink]="['/growth/search-ops']" [queryParams]="propertyQuery()" [label]="t('growth.searchops.workHome.actions.home')"></a>
        <a pButton severity="secondary" [routerLink]="['/growth/search-ops/reports']" [queryParams]="propertyQuery()" [label]="t('growth.searchops.workHome.actions.reports')"></a>
        <a pButton severity="secondary" [routerLink]="['/growth/paid-media/roas']" [label]="t('growth.searchops.sem.actions.roas')"></a>
        <span class="live-badge">{{ t(liveStatusKey(liveUpdateService.connectionState())) }}</span>
      </div>

      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (error()) {
        <ain-state type="error" [title]="t('common.error')" [message]="error()" />
      } @else if (properties().length === 0) {
        <ain-state type="empty" [title]="t('growth.searchops.workHome.empty.title')" [message]="t('growth.searchops.workHome.empty.message')" />
      } @else {
        <section class="card">
          <ain-form-field [label]="t('growth.searchops.fields.property')">
            <p-select
              [options]="propertyOptions()"
              optionLabel="label"
              optionValue="value"
              [(ngModel)]="selectedPropertyId"
              appendTo="body"
              (onChange)="onPropertyChange($event.value)" />
          </ain-form-field>
        </section>

        <section class="card">
          <div class="section-title">
            <h3>{{ t('growth.searchops.scorecard.sections.capture') }}</h3>
            <ain-info-hint
              [titleKey]="'growth.searchops.scorecard.captureHelp.title'"
              [descriptionKey]="'growth.searchops.scorecard.captureHelp.description'" />
          </div>
          <ain-form-field [label]="t('growth.searchops.fields.periodStart')">
            <ain-date-input [(ngModel)]="periodStartUtc" />
          </ain-form-field>
          <ain-form-field [label]="t('growth.searchops.fields.periodEnd')">
            <ain-date-input [(ngModel)]="periodEndUtc" />
          </ain-form-field>
          <ain-form-field [label]="t('growth.searchops.fields.source')">
            <p-select [options]="sourceOptions()" optionLabel="label" optionValue="value" [(ngModel)]="source" appendTo="body" />
          </ain-form-field>
          <ain-structured-json-form [fields]="metricFields" [(payloadJson)]="metricsJson" />
          <button pButton type="button" [disabled]="busy() || !selectedPropertyId" [label]="t('growth.searchops.scorecard.actions.save')" (click)="saveScorecard()"></button>
        </section>

        <section class="card">
          <div class="section-title">
            <h3>{{ t('growth.searchops.scorecard.sections.compare') }}</h3>
            <ain-info-hint
              [titleKey]="'growth.searchops.scorecard.compareHelp.title'"
              [descriptionKey]="'growth.searchops.scorecard.compareHelp.description'" />
          </div>
          @if ((board()?.latest?.comparisons?.length ?? 0) === 0) {
            <ain-state type="empty" [title]="t('growth.searchops.scorecard.compare.empty')" />
          } @else {
            <p-table [value]="board()!.latest!.comparisons" [tableStyle]="{ 'min-width': '40rem' }">
              <ng-template pTemplate="header">
                <tr>
                  <th>{{ t('growth.searchops.fields.metric') }}</th>
                  <th>{{ t('growth.searchops.fields.current') }}</th>
                  <th>{{ t('growth.searchops.fields.previous') }}</th>
                  <th>{{ t('growth.searchops.fields.goalTarget') }}</th>
                  <th>{{ t('growth.searchops.fields.vsPrevious') }}</th>
                  <th>{{ t('growth.searchops.fields.vsGoal') }}</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td>{{ t('growth.searchops.scorecard.metric.' + row.code) }}</td>
                  <td>{{ formatNumber(row.current) }}</td>
                  <td>{{ formatNumber(row.previous) }}</td>
                  <td>{{ formatNumber(row.goalTarget) }}</td>
                  <td>{{ formatNumber(row.vsPreviousDelta) }}</td>
                  <td>{{ formatNumber(row.vsGoalDelta) }}</td>
                </tr>
              </ng-template>
            </p-table>
          }
        </section>

        <section class="card">
          <div class="section-title">
            <h3>{{ t('growth.searchops.scorecard.sections.anomalies') }}</h3>
            <ain-info-hint
              [titleKey]="'growth.searchops.scorecard.anomalyHelp.title'"
              [descriptionKey]="'growth.searchops.scorecard.anomalyHelp.description'" />
          </div>
          <p-table [value]="anomalies()" [paginator]="true" [rows]="10" [tableStyle]="{ 'min-width': '40rem' }">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('growth.searchops.fields.metric') }}</th>
                <th>{{ t('growth.searchops.fields.status') }}</th>
                <th>{{ t('growth.searchops.fields.detectedAt') }}</th>
                <th>{{ t('growth.searchops.fields.hypothesis') }}</th>
                <th></th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td>{{ t('growth.searchops.anomalyMetric.' + row.metricCode) }}</td>
                <td>{{ t('growth.searchops.anomalyStatus.' + row.status) }}</td>
                <td>{{ row.detectedAtUtc }}</td>
                <td><ain-structured-json-display [fields]="hypothesisFields" [payloadJson]="row.hypothesisJson" /></td>
                <td class="actions">
                  @if (!row.taskId) {
                    <button pButton type="button" size="small" [disabled]="busy()" [label]="t('growth.searchops.scorecard.actions.createTask')" (click)="createTask(row)"></button>
                  }
                </td>
              </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
              <tr>
                <td colspan="5">{{ t('growth.searchops.scorecard.anomalies.empty') }}</td>
              </tr>
            </ng-template>
          </p-table>
        </section>

        <section class="card">
          <div class="section-title">
            <h3>{{ t('growth.searchops.scorecard.sections.algoLog') }}</h3>
            <ain-info-hint
              [titleKey]="'growth.searchops.scorecard.algoHelp.title'"
              [descriptionKey]="'growth.searchops.scorecard.algoHelp.description'" />
          </div>
          <ain-form-field [label]="t('growth.searchops.fields.engine')">
            <p-select [options]="engineOptions()" optionLabel="label" optionValue="value" [(ngModel)]="algoEngine" appendTo="body" />
          </ain-form-field>
          <ain-form-field [label]="t('growth.searchops.fields.notedOn')">
            <ain-date-input [(ngModel)]="algoNotedOnUtc" />
          </ain-form-field>
          <ain-form-field [label]="t('growth.searchops.fields.title')">
            <input pInputText [(ngModel)]="algoTitle" />
          </ain-form-field>
          <ain-structured-json-form [fields]="algoFields" [(payloadJson)]="algoEffectJson" />
          <button pButton type="button" [disabled]="busy() || !selectedPropertyId || !algoTitle" [label]="t('growth.searchops.scorecard.actions.addAlgo')" (click)="saveAlgo()"></button>
          <p-table [value]="algoLog()" [paginator]="true" [rows]="10" [tableStyle]="{ 'min-width': '36rem' }">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('growth.searchops.fields.notedOn') }}</th>
                <th>{{ t('growth.searchops.fields.engine') }}</th>
                <th>{{ t('growth.searchops.fields.title') }}</th>
                <th>{{ t('growth.searchops.fields.observedEffect') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td>{{ row.notedOnUtc }}</td>
                <td>{{ t('growth.searchops.algoEngine.' + row.engine) }}</td>
                <td>{{ row.title }}</td>
                <td><ain-structured-json-display [fields]="algoFields" [payloadJson]="row.observedEffectJson" /></td>
              </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
              <tr>
                <td colspan="4">{{ t('growth.searchops.scorecard.algo.empty') }}</td>
              </tr>
            </ng-template>
          </p-table>
        </section>
      }
    </ain-page>
  `,
  styles: [`
    .toolbar, .section-title, .actions { display: flex; flex-wrap: wrap; gap: 0.75rem; align-items: center; margin-bottom: 1rem; }
    .live-badge { margin-inline-start: auto; }
    .card { margin-bottom: 1.25rem; }
    h3 { margin: 0; }
    td { white-space: normal; overflow-wrap: anywhere; }
  `]
})
export class GrowthSearchOpsScorecardComponent implements OnInit {
  private readonly api = inject(GrowthSearchOpsService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly localization = inject(LocalizationService);
  private readonly context = inject(CurrentContextService);
  readonly liveUpdateService = inject(LiveUpdateService);
  private readonly destroyRef = inject(DestroyRef);
  readonly metricFields = searchOpsScorecardMetricFields;
  readonly hypothesisFields = searchOpsAnomalyHypothesisFields;
  readonly algoFields = searchOpsAlgoEffectFields;
  readonly liveStatusKey = liveStatusTranslationKey;

  readonly loading = signal(true);
  readonly busy = signal(false);
  readonly error = signal('');
  readonly properties = signal<SearchPropertyListItemDto[]>([]);
  readonly board = signal<SearchScorecardBoardDto | null>(null);
  readonly anomalies = signal<SearchAnomalyDto[]>([]);
  readonly algoLog = signal<SearchAlgoLogEntryDto[]>([]);
  selectedPropertyId: string | null = null;
  periodStartUtc = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
  periodEndUtc = new Date().toISOString();
  source = 'sandbox';
  metricsJson = '{}';
  algoEngine = 'google';
  algoNotedOnUtc = new Date().toISOString();
  algoTitle = '';
  algoEffectJson = '{}';
  private subscribedTopic: string | null = null;

  ngOnInit(): void {
    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      this.selectedPropertyId = params.get('propertyId');
      this.load(true);
    });
    this.liveUpdateService.events$
      .pipe(
        filter(event => event.eventType === LiveUpdateEventTypes.growthSearchOpsChanged),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.load(false));
    void this.setupLiveSubscription();
    this.destroyRef.onDestroy(() => {
      if (this.subscribedTopic) {
        void this.liveUpdateService.unsubscribe(this.subscribedTopic);
      }
    });
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  propertyQuery() {
    return this.selectedPropertyId ? { propertyId: this.selectedPropertyId } : {};
  }

  propertyOptions() {
    return this.properties().map(item => ({ value: item.id, label: `${item.name} (${item.primaryDomain})` }));
  }

  sourceOptions() {
    return SEARCH_OPS_SCORECARD_SOURCES.map(value => ({ value, label: this.t('growth.searchops.scorecardSource.' + value) }));
  }

  engineOptions() {
    return SEARCH_OPS_ALGO_ENGINES.map(value => ({ value, label: this.t('growth.searchops.algoEngine.' + value) }));
  }

  formatNumber(value: number | null): string {
    return value == null ? '—' : `${value}`;
  }

  onPropertyChange(propertyId: string): void {
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { propertyId },
      queryParamsHandling: 'merge'
    });
  }

  saveScorecard(): void {
    if (!this.selectedPropertyId || !this.periodStartUtc || !this.periodEndUtc) {
      return;
    }
    this.busy.set(true);
    this.api.upsertScorecard({
      searchPropertyId: this.selectedPropertyId,
      periodStartUtc: this.periodStartUtc,
      periodEndUtc: this.periodEndUtc,
      source: this.source,
      metricsJson: this.metricsJson
    }).pipe(finalize(() => this.busy.set(false))).subscribe({
      next: () => this.load(false),
      error: err => this.error.set(err?.error?.error ?? this.t('growth.searchops.messages.saveFailed'))
    });
  }

  createTask(row: SearchAnomalyDto): void {
    this.busy.set(true);
    this.api.createTaskFromAnomaly(row.id).pipe(finalize(() => this.busy.set(false))).subscribe({
      next: () => this.load(false),
      error: err => this.error.set(err?.error?.error ?? this.t('growth.searchops.messages.saveFailed'))
    });
  }

  saveAlgo(): void {
    if (!this.selectedPropertyId || !this.algoTitle) {
      return;
    }
    this.busy.set(true);
    this.api.upsertAlgoLog({
      searchPropertyId: this.selectedPropertyId,
      engine: this.algoEngine,
      notedOnUtc: this.algoNotedOnUtc,
      title: this.algoTitle,
      observedEffectJson: this.algoEffectJson
    }).pipe(finalize(() => this.busy.set(false))).subscribe({
      next: () => {
        this.algoTitle = '';
        this.algoEffectJson = '{}';
        this.load(false);
      },
      error: err => this.error.set(err?.error?.error ?? this.t('growth.searchops.messages.saveFailed'))
    });
  }

  load(showSpinner: boolean): void {
    if (showSpinner) {
      this.loading.set(true);
    }
    this.error.set('');
    this.api.listProperties().pipe(finalize(() => this.loading.set(false))).subscribe({
      next: properties => {
        this.properties.set(properties);
        if (!this.selectedPropertyId && properties[0]) {
          this.selectedPropertyId = properties[0].id;
        }
        this.loadBoards();
      },
      error: err => this.error.set(err?.error?.error ?? this.t('growth.searchops.messages.loadFailed'))
    });
  }

  private loadBoards(): void {
    if (!this.selectedPropertyId) {
      return;
    }
    const propertyId = this.selectedPropertyId;
    forkJoin({
      board: this.api.getScorecard(propertyId),
      anomalies: this.api.listAnomalies(propertyId),
      algoLog: this.api.listAlgoLog(propertyId)
    }).subscribe({
      next: payload => {
        this.board.set(payload.board);
        this.anomalies.set(payload.anomalies);
        this.algoLog.set(payload.algoLog);
        if (payload.board.latest) {
          this.periodStartUtc = payload.board.latest.periodStartUtc;
          this.periodEndUtc = payload.board.latest.periodEndUtc;
          this.source = payload.board.latest.source;
          this.metricsJson = payload.board.latest.metricsJson;
        }
      },
      error: err => this.error.set(err?.error?.error ?? this.t('growth.searchops.messages.loadFailed'))
    });
  }

  private async setupLiveSubscription(): Promise<void> {
    const tenantId = this.context.context()?.tenantId;
    if (!tenantId) {
      return;
    }
    this.subscribedTopic = growthSearchOpsTopic(tenantId);
    await this.liveUpdateService.subscribe(this.subscribedTopic);
  }
}
